import { businessRangeQuery, isoToBusinessClock, isoToBusinessDate } from 'config/time';
import { useTranslation } from 'react-i18next';
import React from "react";
import { MdDownload } from "react-icons/md";
import { fetchAll } from 'config/fetchAll';
import { CSV_DEVICE_TIME_ZONE_HEADER, CSV_DEVICE_UTC_OFFSET_HEADER, csvDeviceTimeZone, csvDeviceUtcOffset, csvFilename, csvUtcOffset, csvUtcOffsetHeader, csvZoneLabel, downloadCsv, toCsv } from 'config/csv';
import axiosInstance from 'config/axiosInstance';
import { ClientSelection, isTargetClient } from 'components/Client/Utility/selection';
import { isCountedSession, summarizeUrl } from 'components/Client/Utility/summary';
import { secondsToHoursText, timeStringToSeconds } from 'components/Client/Utility/timeHelpers';

interface ExportCSVProps {
    showClientHour: boolean;
    dateRange: { start: string, end: string };
    /** ADM-12。一覧・グラフと同じ作業・配送先の選択。 */
    selection?: ClientSelection;
}

const NO_SELECTION: ClientSelection = { taskId: null, clientIds: [] };

/** ADM-12。選んだ作業・配送先に合う作業記録か。 */
const isTargetSession = (session: any, selection: ClientSelection) => {
    if (selection.clientIds.length > 0 &&
        !selection.clientIds.map(String).includes(String(session.client_delivery_info?.id ?? session.client_delivery))) {
        return false;
    }
    if (selection.taskId !== null && selection.taskId !== undefined) {
        return String(session.task_info?.id ?? session.task) === String(selection.taskId);
    }
    return true;
};

/**
 * ADM-21（2026-10-11 PM 判断）。作業時間は小数の時間（小数2桁。例 26.50）で出す。
 * グラフ（ClientGraph の小数の時間）と同じ単位。以前は「1d 2hrs 30min 0sec」の英語の表記だった。
 */
const DURATION_HEADER = "Total Duration (Hours)";
// PR #224 の照合（HR-AC3）。丸めはグラフと同じ関数（整数の演算の四捨五入）で行う。
const secondsToHours = secondsToHoursText;

const ExportCSV: React.FC<ExportCSVProps> = ({ showClientHour, dateRange, selection = NO_SELECTION }) => {
    const { t } = useTranslation('client');

    /**
     * ADM-21-AC2。グラフ（集計 API）が数える作業記録だけを読む。期間は開始で問い合わせ、
     * 終了していない作業・期間の終わりをまたぐ作業は集計 API と同じく除く。
     */
    const fetchCountedSessions = async () => {
        // BE-21。件数をpage_sizeに渡すとサーバー上限（100件）で切れる。
        const sessions = await fetchAll<any>(`/v1/task_sessions/?${businessRangeQuery('start_time', dateRange.start, dateRange.end)}`);
        return sessions.filter((session: any) =>
            isTargetSession(session, selection) && isCountedSession(session, dateRange));
    };

    const sessionSeconds = (session: any) => {
        const seconds = (Date.parse(session.stop_time) - Date.parse(session.start_time)) / 1000;
        return seconds > 0 ? seconds : 0;
    };

    const handleExportCSV = async () => {
        try {
            const now = new Date();
            let headers: string[] = [];
            const csvData: string[][] = [];
            // EV-116 段階2（2026-10-11 PM 決定6）。業務時間帯で計算した列（日付・開始・終了）の見出しに
            // 時間帯（UTC との差）を付ける。行の中で差が変わる（夏時間をまたぐ）ときは時間帯の名前を付け、
            // 行ごとの差の列を、開始と停止の2列で足す（RV-276。作業そのものが切り替えをまたぐと差が違う）。
            const zoned = (header: string, label: string) => `${header} (${label})`;

            if (showClientHour) {
                const taskSessionResults = await fetchCountedSessions();
                const zone = csvZoneLabel(
                    taskSessionResults.flatMap((session: any) => [session.start_time, session.stop_time]),
                    now
                );

                headers = ["Task Session ID", "Client ID", "Client Name", "Driver ID", zoned("Date", zone.label), "Task Name", zoned("Started At", zone.label), zoned("Stopped At", zone.label), DURATION_HEADER];
                if (zone.perRow) headers.push(csvUtcOffsetHeader("Start"), csvUtcOffsetHeader("Stop"));
                // EV-116 段階3。開始・停止の時点の端末の時間帯（2組。無い記録は空欄）。
                headers.push(
                    `Start ${CSV_DEVICE_TIME_ZONE_HEADER}`, `Start ${CSV_DEVICE_UTC_OFFSET_HEADER}`,
                    `Stop ${CSV_DEVICE_TIME_ZONE_HEADER}`, `Stop ${CSV_DEVICE_UTC_OFFSET_HEADER}`
                );

                taskSessionResults.forEach((session: any) => {
                    const clientId = session.client_delivery_info?.id || "";
                    const clientName = session.client_delivery_info?.name || "";
                    const driverId = session.user?.id || "";
                    const taskName = session.task_info?.name || "";
                    // ADM-05。日付・時刻は業務時間帯（会社の時間帯。端末の時間帯に依存しない）。
                    const startTimeFormatted = isoToBusinessClock(session.start_time);
                    const stopTimeFormatted = isoToBusinessClock(session.stop_time);
                    const totalDuration = secondsToHours(sessionSeconds(session));

                    const row = [session.id ?? "", clientId, clientName, driverId, isoToBusinessDate(session.start_time), taskName, startTimeFormatted, stopTimeFormatted, totalDuration];
                    const withOffsets = zone.perRow ? [...row, csvUtcOffset(session.start_time), csvUtcOffset(session.stop_time)] : row;
                    csvData.push([
                        ...withOffsets,
                        csvDeviceTimeZone(session.device_time_zone), csvDeviceUtcOffset(session.device_utc_offset_minutes),
                        csvDeviceTimeZone(session.stop_device_time_zone), csvDeviceUtcOffset(session.stop_device_utc_offset_minutes),
                    ]);
                });
            } else {
                const [clientDeliveryResults, summary, sessions] = await Promise.all([
                    fetchAll<any>('/v1/client_deliveries/'),
                    // ADM-21-AC2。合計はグラフと同じ集計 API の値を使う。
                    axiosInstance.get<{ id: number; hour_spent: string | null }[]>(
                        summarizeUrl('without_tasks', selection, dateRange)
                    ),
                    fetchCountedSessions(),
                ]);
                const secondsByClient = new Map<string, number>(
                    summary.data.map((row) => [String(row.id), timeStringToSeconds(row.hour_spent)])
                );

                const zone = csvZoneLabel(
                    sessions.flatMap((session: any) => [session.start_time, session.stop_time]),
                    now
                );
                // 配送先ごとの行で、開始・終了の日付は業務時間帯の暦日。差が変わるときは時間帯の名前だけを付ける
                // （1行に複数の作業をまとめるので、行ごとの差の列は付けない）。
                headers = ["Client ID", "Client Name", "Task Count", zoned("Started At", zone.label), zoned("Stopped At", zone.label), DURATION_HEADER];

                for (const client of clientDeliveryResults.filter((row: any) => isTargetClient(row, selection))) {
                    const clientId = client.id;
                    const clientName = client.name;
                    const taskCount = client.tasks?.length || 0;

                    const own = sessions.filter((session: any) =>
                        String(session.client_delivery_info?.id ?? session.client_delivery) === String(clientId));
                    // 期間の中で最初に始めた日と、最後に終えた日（業務時間帯）。
                    const startAt = own.length > 0
                        ? isoToBusinessDate(own.reduce((min: any, s: any) => (Date.parse(s.start_time) < Date.parse(min.start_time) ? s : min)).start_time)
                        : "";
                    const stopAt = own.length > 0
                        ? isoToBusinessDate(own.reduce((max: any, s: any) => (Date.parse(s.stop_time) > Date.parse(max.stop_time) ? s : max)).stop_time)
                        : "";

                    const totalDuration = secondsToHours(secondsByClient.get(String(clientId)) ?? 0);
                    csvData.push([clientId, clientName, taskCount.toString(), startAt, stopAt, totalDuration]);
                }
            }

            // ADM-10。共通の CSV 生成を使う。値は引用し（カンマ・改行・引用符で列がずれない）、
            // = + - @ で始まる値は数式として扱わせない。BOM 付きの Blob で出すので # 以降も切れない。
            // ADM-11-AC2。出力した条件・件数・作成日時をファイル名に入れる。
            const filename = csvFilename('client_analysis', [
                ['period', `${dateRange.start}_${dateRange.end}`],
                ['view', showClientHour ? 'tasks' : 'clients'],
                ['task', selection.taskId],
                ['clients', selection.clientIds.join('-')],
            ], csvData.length, now);
            downloadCsv(filename, toCsv(headers, csvData));
        } catch (error) {
            console.error("Failed to export CSV", error);
        }
    };
    return (
        <button
            type="button"
            className="flex rounded-md bg-white text-primary/300 font-medium p-2 justify-center items-center gap-2"
            onClick={handleExportCSV}
        >
            <span className="text-subtitle-2">{t('export_csv')}</span>
            <MdDownload size={20} aria-hidden="true" />
        </button>
    );
};

export default ExportCSV;
